import { useUserInfoStore } from "@/stores/userInfoStore";
import { useQuery } from "@tanstack/react-query";
import { CACHE_KEY } from "constants/general";
import { formatProfileApiData } from "helpers/profile.helper";
import { getProfileDetails } from "services/profile.services";

// Hook to get profile details of logged in user

/*
Note:
This hook fetches profile data from the API and keeps it in Recoil state.
For now, both this hook and direct API calls and recoil state are used in different parts of the app.
Eventually, all profile data management should go through this hook for consistency.
*/
export const useProfileDetails = (
  enabled: boolean = true,
  onSuccess?: (data: any) => void,
  onError?: (error: Error) => void,
  //   only to show some special error message in the console
  customErrorMessage: string = "Failed to fetch profile details",
) => {
  const setProfileData = useUserInfoStore((s) => s.setUserInfo);

  return useQuery({
    queryKey: [CACHE_KEY.PROFILE_QUERY_KEYS.PROFILE_DETAILS],
    queryFn: async () => {
      try {
        const response: any = await getProfileDetails();
        if (response?.status === 200 && response?.data?.data) {
          const profileData = formatProfileApiData(response?.data) || {};
          // Read fresh store state imperatively to avoid stale closure
          const storeState = useUserInfoStore.getState();
          const freshUserInfo = storeState.userInfo || {};
          const isLocalOverrideActive =
            storeState.optimisticUpdatedUntil > Date.now();

          const dataToSetInStore = isLocalOverrideActive
            ? {
                ...profileData,
                ...freshUserInfo,
              }
            : {
                ...freshUserInfo,
                ...profileData,
              };
          setProfileData(dataToSetInStore);
          onSuccess?.(dataToSetInStore);
          return dataToSetInStore;
        }
        const error = new Error(customErrorMessage);
        onError?.(error);
        throw error;
      } catch (err) {
        console.error(err);
        onError?.(err as Error);
        throw err;
      }
    },
    enabled,
  });
};
